import { useState, useEffect, useRef } from 'react';

const C = {
  green:"#16320f",greenLight:"#1f4516",greenBorder:"#3a7029",
  gold:"#d9c20d",bg:"#0c1e09",surface:"#111f0d",
  text:"#e8f0e6",textMid:"#8faa8a",textDim:"#4a6646",teal:"#34d399",
};
const AGE_GROUP_ORDER = {"10U":0,"12U":1,"14U":2,"16U":3,"18U":4};

export default function Schedule() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const gcSdkRef = useRef(false);

  // noindex
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { try { document.head.removeChild(meta); } catch {} };
  }, []);

  useEffect(() => {
    fetch('/api/org-profile')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const sorted = (data.widgetTeams || []).sort((a, b) =>
          (AGE_GROUP_ORDER[a.ageGroup] ?? 99) - (AGE_GROUP_ORDER[b.ageGroup] ?? 99) ||
          (a.rank ?? 99) - (b.rank ?? 99) ||
          a.name.localeCompare(b.name)
        );
        setTeams(sorted);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Load GC SDK once after teams are available and divs are in the DOM
  useEffect(() => {
    if (!teams.length || gcSdkRef.current) return;
    gcSdkRef.current = true;
    const script = document.createElement('script');
    script.src = 'https://widgets.gc.com/static/js/sdk.v1.js';
    script.onload = () => {
      teams.forEach(team => {
        if (window.GC?.team?.schedule?.init) {
          window.GC.team.schedule.init({
            target: `#${team.gcWidgetDivId}`,
            widgetId: team.gcWidgetId,
            maxVerticalGamesVisible: team.gcWidgetMaxGames || 4,
          });
        }
      });
    };
    document.body.appendChild(script);
  }, [teams]);

  // Group by age group
  const byAgeGroup = teams.reduce((acc, t) => {
    (acc[t.ageGroup] = acc[t.ageGroup] || []).push(t);
    return acc;
  }, {});
  const ageGroups = Object.keys(byAgeGroup).sort(
    (a, b) => (AGE_GROUP_ORDER[a] ?? 99) - (AGE_GROUP_ORDER[b] ?? 99)
  );

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'Inter,-apple-system,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&family=Inter:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        a { color: ${C.teal}; }
      `}</style>

      {/* Header */}
      <div style={{ background: C.greenLight, borderBottom: `2px solid ${C.gold}`, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 12, minHeight: 56 }}>
        <img src="/warriors-logo.png" style={{ width: 36, height: 36, objectFit: 'contain' }} alt="Warrior Academy" onError={e => { e.target.style.display = 'none'; }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, letterSpacing: 4, color: C.gold, lineHeight: 1 }}>Warrior Academy</div>
          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: 2 }}>2026 SCHEDULE</div>
        </div>
        <a href="/warriors" style={{ fontSize: 10, color: C.teal, textDecoration: 'none', fontFamily: "'DM Mono',monospace", letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>← Warrior Academy</a>
      </div>

      {/* Sticky age-group nav — matches Manage tab style */}
      {!loading && ageGroups.length > 0 && (
        <div style={{ position: 'sticky', top: 0, zIndex: 10, background: C.bg, borderBottom: `1px solid ${C.greenBorder}`, padding: '7px 24px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          {ageGroups.map((ag, i, arr) => (
            <span key={ag} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button onClick={() => document.getElementById(`ag-${ag}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gold, fontSize: '9px', letterSpacing: '2px', fontFamily: "'DM Mono',monospace", padding: '2px 0', opacity: 0.85 }}>
                {ag}
              </button>
              {i < arr.length - 1 && <span style={{ color: C.greenBorder, fontSize: '10px' }}>·</span>}
            </span>
          ))}
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px' }}>
        {loading && <div style={{ textAlign: 'center', padding: '80px 0', color: C.textDim, fontSize: 13 }}>Loading schedule…</div>}

        {!loading && teams.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textDim, fontSize: 13, border: `1px dashed ${C.greenBorder}`, borderRadius: 8 }}>
            No schedule widgets configured yet.
          </div>
        )}

        {!loading && ageGroups.map(ag => (
          <div key={ag} id={`ag-${ag}`} style={{ marginBottom: 36, scrollMarginTop: 48 }}>
            {/* Age group header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: 3, color: C.gold }}>{ag}</div>
              <div style={{ flex: 1, height: 1, background: C.gold + '44' }} />
            </div>
            {/* Widget grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(460px,100%),1fr))', gap: 16 }}>
              {byAgeGroup[ag].map(team => (
                <div key={team.id} style={{ background: C.surface, border: `1px solid ${C.greenBorder}`, borderRadius: 6, overflowX: 'auto' }}>
                  <div style={{ padding: '8px 14px', borderBottom: `1px solid ${C.greenBorder}`, background: C.green }}>
                    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 17, letterSpacing: 2, color: C.gold }}>{team.name}</div>
                  </div>
                  <div style={{ padding: '12px 14px' }}>
                    <div id={team.gcWidgetDivId} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ textAlign: 'center', padding: '16px 0 8px', fontSize: 11, color: C.textDim }}>
          <a href="/warriors" style={{ color: C.textDim, textDecoration: 'none' }}>← Back to Warrior Academy</a>
        </div>
      </div>
    </div>
  );
}
